/**
 * 67.32: what the solved aim rests on (the owner, Oct 10: "keeps picking the super close object rather than the far
 * object … lying down, trying to shoot around certain objects, or near a ridgeline you're trying to shoot over … similar
 * trying to shoot through trees"). The crosshair's ray marches out from the muzzle along the look; it used to rest on the
 * first thing it touched, so a ridge lip, a rock or a trunk a step away became *here* and the gun solved for it. Now:
 *
 * - **(a) Nothing near is a target.** The ground and the props within AIM_NEAR_PERSONS of the muzzle are looked past —
 *   only a person stops the ray that close — so the solve reaches over the lip, round the rock, past the trunk.
 * - **(b) Concealment is not a target.** Foliage is in no hit shape (63.3), so it never stops the ray; only cover and
 *   the ground do. (Already so; held by a test.)
 * - **(c) Aim at the far thing, say when the muzzle is blocked.** The gun still fires from the muzzle: when the shot's
 *   real path stops near the muzzle short of the target (prone behind a ridge whose lip the arc cannot clear), the
 *   crosshair shows *blocked* rather than re-aiming at the lip.
 * - **(d) A sudden jump near waits.** When the target leaps much nearer (a trunk swinging across the line), the old
 *   target is held for AIM_NEAR_WAIT_S before the converge follows, so a passing obstruction does not yank the view.
 *
 * Presentation of aim only: the sim's shots are untouched. Pure functions; the host (actionHost.ts) supplies the world.
 */
import type { GameState, PlanetState, PlayerState, Vec3 } from '../types';
import { predictShot, solveAimPitch } from '../core/aimSolve';
import { dragAimSpeed, fallTime2, flightTime } from '../core/ballistics';
import { gravityAccel } from '../core/physics';
import { PHYS_DT } from '../constants';
import { weatherSees } from '../core/perception';
import { hitboxCenter, segmentCovered } from '../core/bullets';
import { personEyePoint, personLineOfSight } from '../core/fineTerrain';
import type { PersonSet } from '../core/scaleProfile';
import { crownHides } from '../core/foliage';

let nearOverride: boolean | null = null;
/** Decision 147: on, its Lab row and `?aimnear` retired; the setter is kept for a game or design toggle later (null = on). */
export function setAimNear(on: boolean | null): void { nearOverride = on; }
/** Whether these rules are in force (on unless a toggle turns them off: the old first-touch aim). */
export function aimNearOn(): boolean { return nearOverride ?? true; }

/** Within this many person-heights of the muzzle, the ground and props do not stop the aim ray (people still do). */
export const AIM_NEAR_PERSONS = 2;
/** How long a sudden jump to a much nearer target waits before the converge follows (seconds). */
export const AIM_NEAR_WAIT_S = 0.15;

/** What a point along the aim ray is in: a person (an enemy, or a friend the shot would hit), the ground, a prop, or nothing. */
export type AimHit = 'person' | 'friend' | 'ground' | 'prop' | null;

export interface AimTarget { target: Vec3 | null; range: number; friend: boolean }

/**
 * March from `muzzle` along `ray` (unit) in `step`s, at most `steps` of them, to the first point `hit` says stops the
 * aim — the ground and props only beyond `near` of the muzzle (rule a).
 */
export function chooseAimTarget(muzzle: Vec3, ray: Vec3, step: number, steps: number, near: number, hit: (p: Vec3) => AimHit): AimTarget {
  for (let i = 1; i <= steps; i++) {
    const d = i * step;
    const p = { x: muzzle.x + ray.x * d, y: muzzle.y + ray.y * d, z: muzzle.z + ray.z * d };
    const h = hit(p);
    if (h === null) continue;
    if ((h === 'ground' || h === 'prop') && d < near) continue;
    return { target: p, range: d, friend: h === 'friend' };
  }
  return { target: null, range: steps * step, friend: false };
}

/**
 * 65.45 (a) (the owner: "fix and figure out 3rd person aiming once and for all"): the crosshair's search out to the round's
 * reach — it stopped at 80 people (320 steps of a quarter person), past which the crosshair rested on nothing and the gun
 * fired along the look with no drop solved. A quarter person a step out to AIM_FINE_PERSONS (where the fights are, as
 * before), then each step grows with the range (AIM_STEP_GROWTH of the distance past it), the edge found again by halves to a
 * quarter person. A coarse step can pass through a person, so the bodies past the fine reach are found on the ray itself
 * (firstBodyAlong). Measured (a 16 v 16 human skirmish's planet, 64 headings): 0.28–0.29 ms a frame before, 0.39–0.44 after (+0.1–0.15 ms; one search a frame, the player's only).
 */
export const AIM_FINE_PERSONS = 80;
export const AIM_STEP_GROWTH = 0.02;
/** 65.45 (c): the camera's converge comes in no nearer than this (people): a person at arm's length is on the gun's line (it was 2.7). */
export const AIM_CONVERGE_MIN_PERSONS = 1;

/** chooseAimTarget out to `reach`: `step` to `fine`, then growing (AIM_STEP_GROWTH × the distance past `fine`). */
export function marchAimTarget(muzzle: Vec3, ray: Vec3, step: number, fine: number, reach: number, near: number, hit: (p: Vec3) => AimHit): AimTarget {
  const at = (d: number) => ({ x: muzzle.x + ray.x * d, y: muzzle.y + ray.y * d, z: muzzle.z + ray.z * d });
  let last = 0;
  for (let d = step; d <= reach; d += d < fine ? step : step + (d - fine) * AIM_STEP_GROWTH) {
    const h = hit(at(d));
    if (h === null) { last = d; continue; }
    if ((h === 'ground' || h === 'prop') && d < near) { last = d; continue; }
    // (past the fine reach a step is long: the edge found again by halves, to a step's length)
    let lo = last, hi = d, hh = h;
    while (hi - lo > step) { const mid = (lo + hi) / 2, hm = hit(at(mid)); if (hm === null) lo = mid; else { hi = mid; hh = hm; } }
    return { target: at(hi), range: hi, friend: hh === 'friend' };
  }
  return { target: null, range: reach, friend: false };
}

/**
 * 65.45 (a): the first body the ray passes within its radius of, between `from` and `to` along it (each body a sphere about its
 * middle: a person's half-height) — the far search's bodies, which its growing step could step through. Null: none.
 */
export function firstBodyAlong(muzzle: Vec3, ray: Vec3, from: number, to: number, bodies: ReadonlyArray<{ centre: Vec3; radius: number; friend: boolean }>): { range: number; friend: boolean } | null {
  let best: { range: number; friend: boolean } | null = null;
  for (const b of bodies) {
    const x = b.centre.x - muzzle.x, y = b.centre.y - muzzle.y, z = b.centre.z - muzzle.z;
    const t = x * ray.x + y * ray.y + z * ray.z;
    if (t < from - b.radius || t > to + b.radius) continue;
    const off2 = x * x + y * y + z * z - t * t;
    if (off2 > b.radius * b.radius) continue;
    const range = t - Math.sqrt(b.radius * b.radius - off2);   // (where the ray enters it)
    if (range < from || range > to) continue;
    if (!best || range < best.range) best = { range, friend: b.friend };
  }
  return best;
}

/** Rule (c): the shot's real landing `landing` stops within `near` of the muzzle while the target lies beyond it. */
export function muzzleBlocked(muzzle: Vec3, landing: Vec3, target: Vec3 | null, near: number): boolean {
  if (!target) return false;
  const dl = Math.hypot(landing.x - muzzle.x, landing.y - muzzle.y, landing.z - muzzle.z);
  const dt = Math.hypot(target.x - muzzle.x, target.y - muzzle.y, target.z - muzzle.z);
  return dl < near && dt > near + 1e-9 && dt - dl > 0.5 * near;
}

export interface AimHold { target: Vec3 | null; range: number; waited: number }

/**
 * Rule (d): the target to use this frame. A new target less than half as far as the held one waits `wait` seconds of
 * `dt`s (the held one stays) before it is taken; any other change is taken at once.
 */
export function holdAimTarget(held: AimHold | null, next: AimTarget, dt: number, wait = AIM_NEAR_WAIT_S): AimHold {
  if (held && held.target && next.range < 0.5 * held.range) {
    const waited = held.waited + dt;
    if (waited < wait) return { target: held.target, range: held.range, waited };
  }
  return { target: next.target, range: next.range, waited: 0 };
}

// ---------------------------------------------------------------------------
// 65.25a: aim assist for the player (the owner, Oct 11: "better auto-aim but just for the player. Ideally it just fires
// with solutions that are better for actual targets downrange, rather than snapping to or something. But only for the
// player, bots are much better at shooting than me")
// ---------------------------------------------------------------------------

/** The assist cone's choices, degrees (0: none — the solve for what the crosshair rests on, as before). */
export const AIM_ASSISTS = ['0', '1', '2', '4'] as const;
export type AimAssist = typeof AIM_ASSISTS[number];
export const AIM_ASSIST_DEFAULT: AimAssist = '2';

let assistOverride: number | null = null;
/** The Lab's launch: the cone in degrees; null goes back to the page's `?aimassist=`, else the default. */
export function setAimAssist(deg: number | null): void { assistOverride = deg; }
/** The cone in force, degrees. */
export function aimAssistDeg(): number {
  if (assistOverride !== null) return assistOverride;
  try {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('aimassist') : null;
    return q !== null && (AIM_ASSISTS as readonly string[]).includes(q) ? Number(q) : Number(AIM_ASSIST_DEFAULT);
  } catch { return Number(AIM_ASSIST_DEFAULT); }
}

/**
 * The body the shot's solution is for, and the point of it: of `bodies` (an enemy's middle `at` and its `head` each), the
 * point nearest the crosshair's line (from `muzzle` along `ray`, a unit vector) within `cone` radians and `reach` of the
 * muzzle that the player sees (`sees(body, point)`, asked only of the candidates, nearest first, so a line test is spent on
 * the few in the cone); a nearer body first at an equal angle. Null: none (the solve stays on what the crosshair rests on).
 * The view never moves: this chooses the solution, not where the crosshair is.
 *
 * 65.25c (the owner: "enemies backlighted looking over a ridge, the auto-aim biases towards the terrain and keeps snapping
 * down to ridge in front of them"): the cone is the head's as well as the middle's — a head over a crest under the
 * crosshair had its middle half a person lower, out of a 2° cone at ten people, so the body was never a candidate and the
 * solve rested on the ridge; now the head, seen, wins.
 */
export function assistTarget<T extends { at: Vec3; head?: Vec3 }>(muzzle: Vec3, ray: Vec3, bodies: readonly T[], cone: number, reach: number, sees: (b: T, point: Vec3) => boolean): (T & { angle: number }) | null {
  if (!(cone > 0)) return null;
  const cands: Array<{ b: T; point: Vec3; angle: number; d: number }> = [];
  const cosCone = Math.cos(cone);
  for (const b of bodies) {
    for (const point of b.head ? [b.at, b.head] : [b.at]) {
      const x = point.x - muzzle.x, y = point.y - muzzle.y, z = point.z - muzzle.z, d = Math.hypot(x, y, z);
      if (!(d > 0) || d > reach) continue;
      const c = (x * ray.x + y * ray.y + z * ray.z) / d;
      if (c < cosCone) continue;
      cands.push({ b, point, angle: Math.acos(Math.min(1, c)), d });
    }
  }
  cands.sort((a, b) => a.angle - b.angle || a.d - b.d);
  for (const c of cands) if (sees(c.b, c.point)) return { ...c.b, at: c.point, angle: c.angle };
  return null;
}

/**
 * 65.25f (the owner: "auto aim still jumps around … it just drags your crosshair around a lot when you look at ridges or
 * anything where there's higher terrain between you and your target"): the pick held. With ground between, the visible
 * point of a body (65.25c: its head or its middle) comes and goes as the view moves, and each flip moved the solved aim
 * (head, middle, or the ridge behind the crosshair when neither was seen). Now the body chosen keeps the solution while
 * either of its points is seen within ASSIST_KEEP_CONE × the cone, its own point first (the head stays the head while
 * seen); unseen, it is held ASSIST_LOST_S at its point before the solve goes back to the crosshair; another body takes it
 * only by ASSIST_SWITCH_MARGIN × the cone nearer the line.
 */
export const ASSIST_KEEP_CONE = 1.5;
export const ASSIST_LOST_S = 0.3;
export const ASSIST_SWITCH_MARGIN = 0.35;
/** 65.25f: the body the assist holds — its id, whether its head is the point, and when a point of it was last seen. */
export interface AssistHold { id: number; head: boolean; seen: number }

/**
 * 65.25f: assistTarget with the pick held (above): `held` from the last frame, `now` in seconds. Returns the pick (null:
 * none) and the hold to keep. `sees` is asked of at most the fresh pick's candidates and the held body's two points.
 */
export function steadyAssistTarget<T extends { id: number; at: Vec3; head?: Vec3 }>(held: AssistHold | null, muzzle: Vec3, ray: Vec3, bodies: readonly T[], cone: number, reach: number, sees: (b: T, point: Vec3) => boolean, now: number): { pick: (T & { angle: number }) | null; hold: AssistHold | null } {
  const fresh = assistTarget(muzzle, ray, bodies, cone, reach, sees);
  const hold = (pick: (T & { angle: number }) | null, head: boolean, seen: number) => ({ pick, hold: pick ? { id: pick.id, head, seen } : null });
  const freshHead = (p: (T & { angle: number }) | null) => !!p && bodies.some(b => b.id === p.id && b.head === p.at);
  const b = held ? bodies.find(x => x.id === held.id) : undefined;
  if (!held || !b || !(cone > 0)) return hold(fresh, freshHead(fresh), now);
  const angleTo = (p: Vec3) => {
    const x = p.x - muzzle.x, y = p.y - muzzle.y, z = p.z - muzzle.z, d = Math.hypot(x, y, z);
    return d > 0 && d <= reach ? Math.acos(Math.max(-1, Math.min(1, (x * ray.x + y * ray.y + z * ray.z) / d))) : Infinity;
  };
  const keep = cone * ASSIST_KEEP_CONE;
  // its own point first, then its other (a head over the crest stays the head while seen; the middle when the head is lost)
  const points = (held.head && b.head ? [{ p: b.head, head: true }, { p: b.at, head: false }] : [{ p: b.at, head: false }, ...(b.head ? [{ p: b.head, head: true }] : [])]);
  for (const q of points) {
    const a = angleTo(q.p);
    if (a > keep || !(fresh?.id === b.id && fresh.at === q.p) && !sees(b, q.p)) continue;
    // another body nearer the line by the margin takes it
    if (fresh && fresh.id !== b.id && fresh.angle < a - cone * ASSIST_SWITCH_MARGIN) return hold(fresh, freshHead(fresh), now);
    return hold({ ...b, at: q.p, angle: a }, q.head, now);
  }
  // unseen: held a moment at its own point, while it stays in the wider cone
  const own = held.head && b.head ? b.head : b.at, a = angleTo(own);
  if (now - held.seen < ASSIST_LOST_S && a <= keep) return { pick: { ...b, at: own, angle: a }, hold: held };
  return hold(fresh, freshHead(fresh), now);
}

/**
 * 65.25f: the assist's correction eased — `cur` toward `want` (radians: the pitch and the yaw the assist adds to the
 * unassisted aim), a first-order approach with time constant ASSIST_EASE_S, each step at most ASSIST_EASE_RATE × `dt`; so
 * the aim steadies while the pick changes, and moving the view (the unassisted aim) is never lagged.
 */
export const ASSIST_EASE_S = 0.08;
export const ASSIST_EASE_RATE = 0.35;   // rad/s: 20°/s, the 4° of yaw in a fifth of a second
/** 65.39: `capped` false — the first-order approach alone (the crosshair's own solution, no pick near: a lob's drop is not held to 20°/s). */
export function easeAssist(cur: { pitch: number; yaw: number }, want: { pitch: number; yaw: number }, dt: number, capped = true): { pitch: number; yaw: number } {
  const k = 1 - Math.exp(-Math.max(0, dt) / ASSIST_EASE_S), cap = capped ? ASSIST_EASE_RATE * Math.max(0, dt) : Infinity;
  const step = (c: number, w: number) => c + Math.max(-cap, Math.min(cap, (w - c) * k));
  return { pitch: step(cur.pitch, want.pitch), yaw: step(cur.yaw, want.yaw) };
}

/**
 * 65.39 (1) (the owner: "still jumpy in terms of terrain"): the solved aim is one correction over the look — what it fires
 * at (the pick's solution, else the crosshair's own) less the look pitch — eased as a whole. It was the crosshair's own
 * solution plus the pick's correction over it, eased: as the crosshair's ray grazed a crest its own solution flipped
 * between the crest and the ground behind it, and each flip moved the aim at once, the pick held or not. Now a held pick's
 * aim does not see the crest at all, and with no pick the flip is eased (ASSIST_EASE_S). Within ASSIST_RETURN_S of a pick,
 * the correction moves at most ASSIST_EASE_RATE (a pick gained, changed or lost); else the approach alone.
 */
export const ASSIST_RETURN_S = 0.5;
export function solvedAimWant(look: number, plain: number | null, assisted: number | null, yaw: number, clampPitch: (p: number) => number): { pitch: number; yaw: number } {
  const fire = assisted ?? plain;
  return { pitch: fire !== null ? clampPitch(fire) - look : 0, yaw: assisted !== null ? yaw : 0 };
}

/**
 * 65.39 (2) (the owner: "doesn't target gunners on .50s on vehicles or emplaced"): the bodies the assist reads — `u`'s
 * enemies alive on its planet, on foot or seated where they show (a seat that puts its gunner at `seatPos`: the HMG's and
 * the gun pit's chair, a vehicle's hatch — the cupola, the pintle), at the body the rounds meet there (its middle and its
 * head at that seat); a rider inside a hull is not one. Pure.
 */
export function assistBodies(state: GameState, u: Pick<PlayerState, 'teamIndex' | 'planetIndex'>, ps: PersonSet): Array<{ id: number; at: Vec3; head: Vec3; p: PlayerState }> {
  return state.players.filter(o => o.alive && o.teamIndex !== u.teamIndex && o.planetIndex === u.planetIndex && (!o.mount || !!o.seatPos))
    .map(o => ({ id: o.id, at: hitboxCenter(o, state.planets, ps.hitboxCenter, ps), head: personEyePoint(o, state.planets, ps, ps.losEyeHeight), p: o }));
}

/**
 * 65.25a cut 2: where to aim for a body at `at` moving at `vel` (world units a second), a round leaving `muzzle` at `speed`
 * under a sideways `wind` acceleration: the body where it will be when the round arrives, less the wind's drift over the
 * flight (½ a t²) — two passes on the flight time. The fall is the pitch solve's (it takes gravity).
 */
export function leadPoint(muzzle: Vec3, at: Vec3, vel: Vec3, speed: number, wind: Vec3 = { x: 0, y: 0, z: 0 }, drag = 0): Vec3 {
  let p = at;
  for (let k = 0; k < 2; k++) {
    const t = flightTime(speed, drag, Math.hypot(p.x - muzzle.x, p.y - muzzle.y, p.z - muzzle.z)), w = fallTime2(speed, drag, t);   // 65.30: under its round's drag
    p = { x: at.x + vel.x * t - wind.x * w, y: at.y + vel.y * t - wind.y * w, z: at.z + vel.z * t - wind.z * w };
  }
  return p;
}

/** 65.25e: the flight the human's assist solves against — the round's own numbers and the wind along its path. */
export interface WindFlight {
  speed: number; life: number; fall: number; gravityScale: number; planets: readonly PlanetState[]; stepLength: number;
  /** 65.30: its round's drag (core/ballistics.ts; absent: none). */
  drag?: number;
  /** The wind's pull on the round at `p`, `t` seconds into its flight (core/wind.ts actionWindAccel by the weapon's share). */
  wind: (p: Vec3, t: number) => Vec3 | null;
  /** Seconds of flight between the wind's samples (windAimStep: a sample costs ~6 µs, a rocket flies 2 s). */
  windStep: number;
}
/**
 * 65.25e: the solve's passes (each one flight; one more without the wind, once), its tolerance (a share of the range) and
 * the solve redone at 10 Hz (the host keeps its wind offset between).
 */
export const WIND_AIM_PASSES = 2;
export const WIND_AIM_TOL = 0.001;
export const WIND_AIM_EVERY_S = 0.1;
/**
 * 65.25e: seconds of flight between the wind's samples for a round at `speed` — one every 2 people it flies, between a tick
 * (the sim's, a bullet's: it flies 6 people a tick) and 4 (a rocket's: the field barely turns in 33 ms at its pace).
 */
export function windAimStep(speed: number, person: number): number {
  return Math.max(PHYS_DT, Math.min(4 * PHYS_DT, (2 * person) / speed));
}

/**
 * 65.25e (the owner: "in gale force wind, the auto aim doesn't really work … human auto aim should still be able to
 * resolve"): where to aim so a round from `muzzle` lands on `at` under the wind along its real flight — the gusts, the
 * shelter and the fade with height, asked where the round is once a tick (predictShot's field), as the sim flies it.
 * leadPoint's ½·a·t² under the muzzle's wind over-solved: in a storm the round drifts a third less by 90 people than the
 * muzzle's wind says (the field downrange is not the muzzle's), so an assisted round landed past the body on the far
 * side of its drift — a rocket downwind (65.25d). Fixed-point passes: aim (the pitch solve's elevation, the bearing to the
 * aim point), fly to the target's range, move the aim point by the wind's part of the miss — the landing less the same
 * shot's in still air (flown once) — so still air is the closed form's aim exactly. Null out of reach (the caller keeps
 * the first-order lead). The bots keep their lead by skill (62.52). Cost: (passes + 1) flights of predictShot.
 */
export function windAimPoint(muzzle: Vec3, at: Vec3, n: Vec3, f: WindFlight, passes = WIND_AIM_PASSES): Vec3 | null {
  const d = { x: at.x - muzzle.x, y: at.y - muzzle.y, z: at.z - muzzle.z }, dn = d.x * n.x + d.y * n.y + d.z * n.z;
  const flat = { x: d.x - n.x * dn, y: d.y - n.y * dn, z: d.z - n.z * dn }, range = Math.hypot(flat.x, flat.y, flat.z);
  if (!(range > 0)) return null;
  const fwd = { x: flat.x / range, y: flat.y / range, z: flat.z / range };
  const past = (p: Vec3) => (p.x - muzzle.x) * fwd.x + (p.y - muzzle.y) * fwd.y + (p.z - muzzle.z) * fwd.z >= range;
  // the shot the sim fires for an aim point: the elevation the pitch solve gives, along the bearing to it (core/bullets.ts aimDirection)
  const land = (aim: Vec3, wind: WindFlight['wind'] | null): Vec3 | null => {
    const mid = { x: (muzzle.x + aim.x) / 2, y: (muzzle.y + aim.y) / 2, z: (muzzle.z + aim.z) / 2 }, ga = gravityAccel(mid, f.planets as PlanetState[]), gs = f.fall * f.gravityScale;
    const pitch = solveAimPitch(muzzle, aim, n, dragAimSpeed(f.speed, f.drag ?? 0, Math.hypot(aim.x - muzzle.x, aim.y - muzzle.y, aim.z - muzzle.z)), { x: ga.x * gs, y: ga.y * gs, z: ga.z * gs });   // (65.30: the drag's elevation)
    if (pitch === null) return null;
    const a = { x: aim.x - muzzle.x, y: aim.y - muzzle.y, z: aim.z - muzzle.z }, an = a.x * n.x + a.y * n.y + a.z * n.z;
    const h = { x: a.x - n.x * an, y: a.y - n.y * an, z: a.z - n.z * an }, hl = Math.hypot(h.x, h.y, h.z);
    if (!(hl > 0)) return null;
    const c = Math.cos(pitch) / hl, s = Math.sin(pitch);
    const dir = { x: h.x * c + n.x * s, y: h.y * c + n.y * s, z: h.z * c + n.z * s }, dl = Math.hypot(dir.x, dir.y, dir.z);
    const shot = predictShot(muzzle, { x: (dir.x / dl) * f.speed, y: (dir.y / dl) * f.speed, z: (dir.z / dl) * f.speed }, { drag: f.drag, life: f.life, fall: f.fall, gravityScale: f.gravityScale, planets: f.planets, blocked: past, stepLength: f.stepLength, wind, windStep: f.windStep });
    return shot.hit ? shot.at : null;
  };
  const calm = land(at, null);
  if (!calm) return null;
  let aim = at;
  for (let k = 0; k < passes; k++) {
    const blown = land(aim, f.wind);
    if (!blown) return null;
    const e = { x: blown.x - calm.x, y: blown.y - calm.y, z: blown.z - calm.z };
    if (Math.hypot(e.x, e.y, e.z) <= WIND_AIM_TOL * range) break;
    aim = { x: aim.x - e.x, y: aim.y - e.y, z: aim.z - e.z };
  }
  return aim;
}

/** 65.25a cut 2: the signed angle across the ground (radians, + to the right) from `heading` to the direction `d`, at up `n`. */
export function yawToward(n: Vec3, heading: Vec3, d: Vec3): number {
  const dn = d.x * n.x + d.y * n.y + d.z * n.z, t = { x: d.x - n.x * dn, y: d.y - n.y * dn, z: d.z - n.z * dn };
  const r = { x: heading.y * n.z - heading.z * n.y, y: heading.z * n.x - heading.x * n.z, z: heading.x * n.y - heading.y * n.x };
  return Math.atan2(t.x * r.x + t.y * r.y + t.z * r.z, t.x * heading.x + t.y * heading.y + t.z * heading.z);
}

/**
 * 65.25b (the owner: "auto-aim doesn't work for emplaced hmg … would like it there as well"): where the assist's sight test
 * looks from — the player's eye on foot; seated at a gun, the gun's muzzle. A seated gunner's eye is low behind the HMG's
 * own sandbag ring, so from it every enemy but one straight through the ring's gap was covered (the gun fires over the
 * ring from its muzzle) — the assist only ever engaged through the gap.
 */
export function assistEye(u: Pick<PlayerState, 'mount'>, eye: Vec3, muzzle: Vec3): Vec3 {
  return u.mount ? muzzle : eye;
}

/**
 * The assist's sight test (the bots' look's: the weather's sight, the ground's line, cover, crowns) from `from` to a body's
 * point `at` — `body` its person (its stance for the crowns). Pure.
 */
export function assistSees(state: GameState, planetIndex: number, from: Vec3, at: Vec3, body: Pick<PlayerState, 'airborne' | 'stance'>, seconds: number, sight: number, coverStep: number): boolean {
  return weatherSees(state, planetIndex, from, at, seconds, sight) && personLineOfSight(from, at, state)
    && !segmentCovered(from, at, state, undefined, coverStep) && !crownHides(state, planetIndex, from, at, body.airborne ? undefined : body.stance);
}

/**
 * 65.45 (1) (the owner: "gl40 and rocket launcher shouldn't have aim assist circles or additional resolution, but should have
 * expected shot marker, currently it makes it really hard to shoot a rocket at the feet of a few people, instead it goes for
 * body strikes"): the assist is for bullets — a launcher (a round that bursts: the LAW, the 40 mm, the Panzerfaust) gets no
 * pick and no pull onto a body; it goes where the crosshair rests (the solve for the crosshair's own point, its fall and its
 * wind), and its landing shows as the impact marker (`showImpact`), always — near the crosshair too.
 */
export function assistAppliesTo(spec: { blast?: unknown }): boolean { return !spec.blast; }
/** 65.45 (1): the landing's marker on screen — a launcher's always (the impact marker); a bullet's dot only where it parts from the crosshair (9 px). */
export function showLanding(px: number, launcher: boolean): boolean { return launcher || px >= 9; }

/**
 * 65.45 (b) (the owner: "fix and figure out 3rd person aiming once and for all"): the view held through a crest. The camera
 * converges at what the crosshair rests on, so when the crosshair's ray grazes a crest its range flips — the crest at 20
 * people, the ground behind at 200 — and the whole view swung about 1.8° each flip (the camera rides off the shoulder).
 * Now a jump of more than CONVERGE_JUMP × either way is taken only once it has held CONVERGE_HOLD_S (a flicker never is);
 * and the camera glides the converge's inverse (`glideConverge`), which is the view's angle off the line (an even swing,
 * not one that comes all at the near end). The shot is untouched: during a hold the dot marks where it lands.
 */
export const CONVERGE_HOLD_S = 0.25;
export const CONVERGE_JUMP = 2;
export interface ConvergeHold { range: number; pending: number | null; waited: number }
export function holdConverge(held: ConvergeHold | null, next: number, dt: number): ConvergeHold {
  if (!held) return { range: next, pending: null, waited: 0 };
  const ratio = (a: number, b: number) => (a > b ? a / b : b / a);
  if (ratio(next, held.range) <= CONVERGE_JUMP) return { range: next, pending: null, waited: 0 };
  const waited = held.pending !== null && ratio(next, held.pending) <= CONVERGE_JUMP ? held.waited + dt : dt;
  return waited >= CONVERGE_HOLD_S ? { range: next, pending: null, waited: 0 } : { range: held.range, pending: next, waited };
}
/** The camera's converge one frame on toward `want`: its inverse (the view's angle off the line) eased by `k`. */
export function glideConverge(drawn: number, want: number, k: number): number {
  return 1 / (1 / drawn + (1 / want - 1 / drawn) * k);
}
